<template>
  <div>
    <MarketingHero
      :pre-title="t('hero.preTitle')"
      title-keypath="hero.title"
      :highlighted-title="t('hero.highlight')"
      :subtitle="t('hero.subtitle')"
      :proof="{
        text: t('hero.proof'),
        icon: ['fal', 'shield-check'],
      }"
    >
      <p>{{ t('hero.content') }}</p>

      <template #cta>
        <TButton
          :to="localePath({
            name: ROUTE_BASE_NAME.rv_rentals,
          })"
          variant="highlight"
          size="lg"
        >
          {{ t('ctaButton') }}
        </TButton>
      </template>

      <template #image>
        <TImage
          :src="heroImage"
          alt=""
          class="aspect-3/2 object-cover rounded-xl"
        />
      </template>
    </MarketingHero>

    <TMarketingSection
      :pre-title="t('valueProp1.preTitle')"
      :title="t('valueProp1.title')"
      :sub-title="t('valueProp1.paragraph')"
      variant="light"
    >
      <div class="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <TCard
          v-for="benefit in valueProp1Benefits"
          :key="benefit.icon"
          class="p-6 transition hover:shadow-md hover:border-primary-200"
        >
          <div class="flex size-12 items-center justify-center rounded-lg bg-primary-50 text-secondary mb-4 mx-auto">
            <TIcon
              :icon="['fal', benefit.icon]"
              size="2xl"
            />
          </div>
          <THeading
            :level="6"
            tag="h3"
            class="mb-2 justify-center"
          >
            {{ benefit.title }}
          </THeading>
          <p class="text-sm text-gray-500 leading-relaxed">
            {{ benefit.description }}
          </p>
        </TCard>
      </div>
    </TMarketingSection>

    <TMarketingSection
      :pre-title="t('sectionPreTitle')"
      :title="t('sectionTitle')"
      :sub-title="t('sectionIntroParagraphs')"
    >
      <div class="mt-8 grid grid-cols-2 md:grid-cols-4 gap-8">
        <TMarketingCard
          v-for="category in categoryLinks"
          :key="category.title"
          :title="category.title"
          :subtitle="category.subtitle"
          :image="category.image"
          :to="category.url"
        />
      </div>
    </TMarketingSection>

    <TMarketingSection
      :pre-title="t('valueProp2.preTitle')"
      :title="t('valueProp2.title')"
      :sub-title="t('valueProp2.paragraph')"
      variant="muted"
      layout="two-columns"
    >
      <template #secondary>
        <div class="grid gap-4 sm:grid-cols-2">
          <div
            v-for="benefit in valueProp2Benefits"
            :key="benefit.title"
            class="bg-white rounded-xl p-6 shadow-sm"
          >
            <div class="flex size-10 items-center justify-center rounded-lg bg-secondary text-white mb-3">
              <TIcon
                :icon="['fal', benefit.icon]"
                size="lg"
              />
            </div>
            <h3 class="text-base font-semibold mb-1">
              {{ benefit.title }}
            </h3>
            <p class="text-sm text-gray-500 leading-relaxed">
              {{ benefit.description }}
            </p>
          </div>
        </div>
      </template>
    </TMarketingSection>

    <!-- Final CTA -->
    <TMarketingSection
      :title="t('ctaTitle')"
      :sub-title="t('ctaParagraphs')"
      variant="dark"
    >
      <template #pre-title>
        <TIcon
          class="[--fa-primary-color:var(--color-white)] [--fa-secondary-color:var(--color-primary-500)] [--fa-secondary-opacity:1]"
          :icon="['fad', 'calendar-check']"
          size="3xl"
        />
      </template>

      <div class="flex flex-col sm:flex-row gap-4 justify-center pt-2">
        <TButton
          :to="localePath({
            name: ROUTE_BASE_NAME.rv_rentals,
          })"
          variant="highlight"
          size="lg"
        >
          {{ t('ctaButton') }}
        </TButton>
      </div>
    </TMarketingSection>
  </div>
</template>

<script setup lang="ts">
import type { IconName } from '@fortawesome/fontawesome-svg-core'
import { ROUTE_BASE_NAME } from '~/constants'
import { EVENT_CATEGORY_IMAGES, EVENT_CATEGORY_SLUGS } from '~/constants/events-categories'

definePageMeta({
  layout: 'static',

  i18n: {
    paths: {
      en: '/events',
      fr: '/evenements',
    },
  },
})

const { locale, t } = useI18n()
const localePath = useLocalePath()

const { getStaticImage, presets } = useImage()
const heroImage = computed(() => getStaticImage({
  path: '/marketing/events-setup.jpg',
  preset: presets.NATIVE_LG,
}))

const valueProp1Benefits = computed<{ icon: IconName, title: string, description: string }[]>(() => [
  {
    icon: 'badge-dollar',
    title: t('valueProp1.benefits.noMarkup.title'),
    description: t('valueProp1.benefits.noMarkup.description'),
  },
  {
    icon: 'location-dot',
    title: t('valueProp1.benefits.closerToVenue.title'),
    description: t('valueProp1.benefits.closerToVenue.description'),
  },
  {
    icon: 'house',
    title: t('valueProp1.benefits.privateSpace.title'),
    description: t('valueProp1.benefits.privateSpace.description'),
  },
  {
    icon: 'calendar-check',
    title: t('valueProp1.benefits.flexSchedule.title'),
    description: t('valueProp1.benefits.flexSchedule.description'),
  },
])

const valueProp2Benefits = computed<{ icon: IconName, title: string, description: string }[]>(() => [
  {
    icon: 'calendar-range',
    title: t('valueProp2.benefits.multiDay.title'),
    description: t('valueProp2.benefits.multiDay.description'),
  },
  {
    icon: 'user-group',
    title: t('valueProp2.benefits.groupTravel.title'),
    description: t('valueProp2.benefits.groupTravel.description'),
  },
  {
    icon: 'route',
    title: t('valueProp2.benefits.noCommute.title'),
    description: t('valueProp2.benefits.noCommute.description'),
  },
  {
    icon: 'bed-bunk',
    title: t('valueProp2.benefits.sleepsMore.title'),
    description: t('valueProp2.benefits.sleepsMore.description'),
  },
])

const categoryLinks = computed(() =>
  EVENT_CATEGORY_SLUGS.map(({ type, slug }) => ({
    title: t(`events.categories.${type}.name`),
    subtitle: t(`events.categories.${type}.shortDescription`),
    url: localePath({
      name: ROUTE_BASE_NAME.events_eventCategory,
      params: { eventCategory: slug[locale.value] },
    }),
    image: {
      src: getStaticImage({ path: EVENT_CATEGORY_IMAGES[type], preset: presets.SQUARE_LG }) ?? '',
      alt: '',
    },
  })),
)

useSeoHeadTags({
  title: computed(() => t('seo.title')),
  description: computed(() => t('seo.description')),
})
</script>

<i18n src="~~/i18n/locales/common/events.json" lang="json" />

<i18n lang="json">
{
  "en": {
    "seo": {
      "title": "RV Rentals for Events",
      "description": "Browse RV-friendly events by category: festivals, sports, concerts, and more. Plan your trip and rent an RV near the venue."
    },
    "hero": {
      "preTitle": "Across Canada and the U.S.",
      "title": "RV Rentals for {highlight}",
      "highlight": "events",
      "subtitle": "Renting an RV for an event gives you the freedom to stay close to the action while avoiding hotels, traffic, and sold-out accommodations.",
      "content": "With RVezy, you can rent RVs from local owners near events. Whether you are attending a major sporting event, a multi-day festival, or a race weekend, an RV keeps you flexible and comfortable.",
      "proof": "With insurance included and secure online booking"
    },
    "sectionPreTitle": "Upcoming Events",
    "sectionTitle": "Explore RV-friendly events by type",
    "valueProp1": {
      "preTitle": "Skip the hotel scramble",
      "title": "Stay close to the action",
      "paragraph": "Events drive hotel prices up, fill rooms fast, and scatter your group across town. An RV keeps you close, together, and comfortable.",
      "benefits": {
        "noMarkup": {
          "title": "No hotel markups",
          "description": "Event weekends push hotel rates up. An RV from a local owner costs a fraction of the price and comes without last-minute surcharges."
        },
        "closerToVenue": {
          "title": "Closer to the venue",
          "description": "Park where hotels are sold out. Many RV hosts know the best spots within walking distance of major venues."
        },
        "privateSpace": {
          "title": "Your own private space",
          "description": "A kitchen, a bathroom, and real beds. No sharing walls with strangers or racing back to a distant hotel between sessions."
        },
        "flexSchedule": {
          "title": "Stay on your schedule",
          "description": "Arrive early. Stay late. Leave when you want. No checkout clock, no shuttle dependency."
        }
      }
    },
    "valueProp2": {
      "preTitle": "Stay for the whole event",
      "title": "Built for multi-day events and groups",
      "paragraph": "Festivals, race weekends, and tournament trips run for days. An RV gives your group one base camp, no split bookings, no daily commute.",
      "benefits": {
        "multiDay": {
          "title": "Multi-day ready",
          "description": "Set up once and stay through the whole event. Your campsite is waiting when the last act ends or the final lap is run."
        },
        "groupTravel": {
          "title": "Built for groups",
          "description": "Keep friends and family together under one roof. No juggling multiple hotel rooms or losing people across town."
        },
        "noCommute": {
          "title": "Skip the daily commute",
          "description": "Stay on-site or a short walk from the venue. No two-hour drives back to a distant hotel after every session."
        },
        "sleepsMore": {
          "title": "Sleeps more, costs less",
          "description": "Bunk beds, pull-outs, and full kitchens. RVs often sleep more people than hotel rooms at a lower total cost."
        }
      }
    },
    "ctaTitle": "Find RV rentals for your next event",
    "ctaButton": "Browse RVs near you",
    "sectionIntroParagraphs":  "From music festivals to motorsports — and jump straight into listings for that category.",
    "ctaParagraphs": "Find RV rentals for upcoming events, compare options from local owners, and book online with confidence. Whether you are planning months ahead or organizing a last-minute trip, renting an RV can make event travel simpler and more enjoyable."
  },
  "fr": {
    "seo": {
      "title": "Locations VR pour événements",
      "description": "Parcourez les événements acceptant les VRs par catégorie : festivals, sports, concerts et plus. Planifiez votre voyage et louez un VR près du lieu."
    },
    "hero": {
      "preTitle": "Au Canada et aux États-Unis",
      "title": "Locations VR pour {highlight}",
      "highlight": "événements",
      "subtitle": "Louer un VR pour un événement vous donne la liberté de rester près de l'action tout en évitant les hôtels, le trafic et les réservations épuisées.",
      "content": "Avec RVezy, vous pouvez louer des VRs de propriétaires locaux près des événements.  Que vous participiez à un événement sportif majeur, un festival musical multi-jours ou une fête saisonnière, un VR vous permet de combiner le transport et l'hébergement en une configuration flexible.",
      "proof": "Avec assurance incluse et réservation en ligne sécurisée"
    },
    "sectionPreTitle": "Événements à venir",
    "sectionTitle": "Explorer les types d'événements acceptant les VRs",
    "valueProp1": {
      "preTitle": "Évitez la ruée vers les hôtels",
      "title": "Restez près de l'action",
      "paragraph": "Les événements font monter les prix des hôtels, remplissent les chambres rapidement et dispersent votre groupe en ville. Un VR vous garde près de l'action, ensemble et à l'aise.",
      "benefits": {
        "noMarkup": {
          "title": "Sans majoration hôtelière",
          "description": "Les week-ends d'événements font grimper les tarifs hôteliers. Un VR d'un propriétaire local coûte une fraction du prix, sans supplément de dernière minute."
        },
        "closerToVenue": {
          "title": "Plus près du lieu",
          "description": "Stationnez là où les hôtels sont complets. Plusieurs hôtes VR connaissent les meilleurs emplacements à distance de marche des grands sites."
        },
        "privateSpace": {
          "title": "Votre propre espace privé",
          "description": "Une cuisine, une salle de bain et de vrais lits. Pas de murs partagés avec des inconnus ni de course vers un hôtel éloigné entre les sessions."
        },
        "flexSchedule": {
          "title": "Votre propre horaire",
          "description": "Arrivez tôt. Restez tard. Partez quand vous voulez. Pas d'heure de départ imposée, pas de navette à attendre."
        }
      }
    },
    "valueProp2": {
      "preTitle": "Restez pendant tout l'événement",
      "title": "Idéal pour les événements multi-jours et les groupes",
      "paragraph": "Festivals, week-ends de course et tournois durent plusieurs jours. Un VR offre à votre groupe un camp de base unique, sans réservations éclatées ni trajets quotidiens.",
      "benefits": {
        "multiDay": {
          "title": "Prêt pour plusieurs jours",
          "description": "Installez-vous une fois et restez pendant tout l'événement. Votre emplacement vous attend quand le dernier spectacle se termine ou que la dernière course est courue."
        },
        "groupTravel": {
          "title": "Conçu pour les groupes",
          "description": "Gardez amis et famille sous le même toit. Pas de jonglerie entre plusieurs chambres d'hôtel ni de groupe dispersé en ville."
        },
        "noCommute": {
          "title": "Sans trajet quotidien",
          "description": "Restez sur place ou à quelques pas du lieu. Pas de deux heures de route vers un hôtel éloigné après chaque session."
        },
        "sleepsMore": {
          "title": "Plus de lits, moins cher",
          "description": "Lits superposés, couchages escamotables et cuisines complètes. Un VR loge souvent plus de monde qu'une chambre d'hôtel, pour un coût total plus bas."
        }
      }
    },
    "ctaTitle": "Trouvez un VR à louer pour votre prochain événement",
    "ctaButton": "Parcourir les VR près de chez vous",
    "sectionIntroParagraphs":  "Des festivals de musique au sport mécanique — et accédez directement aux listes pour cette catégorie.",
    "ctaParagraphs": "Trouvez des locations de VR pour les prochains événements, comparez les offres de propriétaires locaux et réservez en ligne en toute confiance. Que vous planifiez des mois à l'avance ou un voyage de dernière minute, louer un VR peut simplifier le voyage événementiel."
  }
}
</i18n>
